import type { ICreateAttributeCmd } from "@/data/commands/catalog/CreateAttributeCmd";
import { Factory } from "@/data/dao/Factory";
import type { DaoError, IDataSource } from "@/data/dao/service/BaseServiceDao";
import { CatalogDao } from "@/data/dao/service/CatalogDao";
import type { IAttribute } from "@/data/model/Attribute";
import { AttributeDisplayType, AttributeEffect, AttributeFlagDisplayTo } from "@/data/model/Enums";
import { exceptionTypes } from "@/data/util/Config";
import { Misc } from "@/data/util/Misc";



import { translate } from "@/shared/localization/language";



import { i18n } from "@/portal/Trans";
import { MultiLanguageInput } from "@/portal/formControls/MultiLanguageInput";
import { NumberInput } from "@/portal/formControls/NumberInput";
import { Select } from "@/portal/formControls/Select";
import { Switch } from "@/portal/formControls/Switch";
import { BaseEntityForm } from "@/portal/forms/BaseEntityForm";


export class AttributeForm extends BaseEntityForm<IAttribute, ICreateAttributeCmd> {
    private isEnabledSwitch: Switch;
    private nameField: MultiLanguageInput;
    private $iconField: JQuery;
    private $iconRow: JQuery;
    private $displayToRow: JQuery;
    private $effectRow: JQuery;
    private $effectHint: JQuery;
    private effectSelect: Select<{ type: AttributeEffect }>;
    private displayTypeSelect: Select<{ type: AttributeDisplayType }>;
    private displayToSelect: Select<{ type: AttributeFlagDisplayTo }>;
    private orderInput: NumberInput;

    // a standard attribute is shared by every location, so only its presentation is the location's own
    private isStandardAttribute = false;

    constructor(element: JQuery) {
        super(element, Factory.getInstance<CatalogDao>(CatalogDao).attributeDataSource);

        this.isEnabledSwitch = new Switch(this.$form.find('input[name="isenabled"]'));

        this.nameField = MultiLanguageInput.fromInput(this.$form.find('.form-control[name="name"]'));
        this.nameField.onChange = (e) => {
            this.updateName(translate(e));
        };

        this.$iconRow = this.$form.find(".icon-row");
        this.$displayToRow = this.$form.find(".display-to-row");
        this.$effectRow = this.$form.find(".effect-row");
        this.$effectHint = this.$effectRow.find(".effect-hint");

        this.displayTypeSelect = Select.createFromSelect(
            this.$form.find('.form-control[name="displaytype"]'),
            this.displayTypeDataSource,
            (c) => i18n("enums.AttributeDisplayType." + AttributeDisplayType[c.type]),
            false,
            undefined,
            "type",
            true
        );
        this.displayTypeSelect.onChange = () => this.updateDisplayTypeVisibility();

        this.displayToSelect = Select.createFromSelect(
            this.$form.find('.form-control[name="displayto"]'),
            this.displayToDataSource,
            (c) => i18n("enums.AttributeFlagDisplayTo." + AttributeFlagDisplayTo[c.type]),
            false,
            undefined,
            "type"
        );

        this.$iconField = this.$form.find('.form-control[name="iconclass"]');

        this.effectSelect = Select.createFromSelect(
            this.$form.find('.form-control[name="attributetype"]'),
            this.effectDataSource,
            (c) => i18n("enums.AttributeEffect." + AttributeEffect[c.type]),
            false,
            undefined,
            "type"
        );
        this.effectSelect.onChange = () => this.updateEffectHint();

        this.orderInput = new NumberInput(this.$form.find('.form-control[name="order"]'), {
            min: 1,
            max: 10000,
            step: 1,
            decimals: 0,
        });

        this.updateDisplayTypeVisibility();
        this.updateEffectHint();
    }

    public setFormData(model?: IAttribute) {
        this.isStandardAttribute = model !== undefined && !model.isCustom;

        this.updateName(model === undefined ? "" : translate(model.name));
        this.isEnabledSwitch.setValue(model === undefined ? false : model.isEnabled);
        this.nameField.setValues(model === undefined ? undefined : model.name);
        this.$iconField.val(model === undefined || model.iconClass === undefined ? "" : model.iconClass);
        this.effectSelect.setValue(model?.effect ?? AttributeEffect.Include);
        this.displayTypeSelect.setValues(model === undefined ? [] : AttributeForm.splitDisplayType(model.displayType));
        this.displayToSelect.setValue(model?.flagDisplayTo ?? AttributeFlagDisplayTo.EndUsersAndOperators);
        this.orderInput.setValue(model === undefined ? undefined : model.sortOrder);
        this.updateDisplayTypeVisibility();
        this.updateEffectHint();
        this.updateStandardAttributeRestrictions();
    }

    private updateStandardAttributeRestrictions() {
        this.nameField.setDisabled(this.isStandardAttribute);
        this.$iconField.prop("disabled", this.isStandardAttribute);
        this.effectSelect.setDisabled(this.isStandardAttribute);

        // the same selector the base form uses for the remove button, which it shows again on every load
        if (this.isStandardAttribute) {
            this.$form.find(".kt-form__actions .removebtn").hide();
        }
    }

    protected resolveErrorMessage(e, b: DaoError): string | undefined {
        if (b?.additionalData?.reason == null) {
            return undefined;
        }

        switch (b.additionalData.reason) {
            case exceptionTypes.standardAttributeCannotBeDeletedException:
                return i18n("attribute.standardCannotBeDeleted");
            case exceptionTypes.attributeCannotBeDisplayedAsFilterException:
                this.displayTypeSelect.setError(true);

                return i18n("attribute.cannotBeDisplayedAsFilter");
            default:
                return undefined;
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public getFormData($form: JQuery): ICreateAttributeCmd {
        const data: ICreateAttributeCmd = {
            name: this.nameField.getValues(),
            isEnabled: this.isEnabledSwitch.getValue(),
            effect: this.hasDisplayType(AttributeDisplayType.Filter)
                ? Number(this.effectSelect.getValue() ?? AttributeEffect.Include)
                : undefined,
            sortOrder: this.orderInput.getValue(),
            iconClass:
                !this.hasDisplayType(AttributeDisplayType.Icon) || this.$iconField.val().length === 0
                    ? undefined
                    : this.$iconField.val(),
            displayType: this.getDisplayType(),
            flagDisplayTo: this.hasDisplayType(AttributeDisplayType.Flag)
                ? Number(this.displayToSelect.getValue() ?? AttributeFlagDisplayTo.EndUsersAndOperators)
                : undefined,
        };

        let hasError = false;
        this.nameField.setError(false);
        this.$iconField.removeClass("is-invalid");

        if (data.displayType === AttributeDisplayType.None) {
            hasError = true;
            this.displayTypeSelect.setError(true);
        } else {
            this.displayTypeSelect.setError(false);
        }

        if (this.hasDisplayType(AttributeDisplayType.Icon) && data.iconClass === undefined) {
            hasError = true;
            this.$iconField.addClass("is-invalid");
        }


        if (
            data.name.de === undefined &&
            data.name.en === undefined &&
            data.name.fr === undefined &&
            data.name.it === undefined
        ) {
            hasError = true;
            this.nameField.setError(true);
        }

        const maxNameLength = 80;
        if (
            ((data.name.de?.length ?? 0) > maxNameLength) ||
            ((data.name.en?.length ?? 0 )> maxNameLength) ||
            ((data.name.fr?.length ?? 0 )> maxNameLength) ||
            ((data.name.it?.length ?? 0 )> maxNameLength)
        ) {
            hasError = true;
            this.nameField.setError(true);
        }

        if (hasError) {
            this.scrollToFirstErrorField();
        }

        return (hasError ? undefined : data) as any;
    }

    private getDisplayType(): AttributeDisplayType {
        return this.displayTypeSelect
            .getValues()
            .reduce((sum, value) => sum + parseInt(value), AttributeDisplayType.None);
    }

    private hasDisplayType(displayType: AttributeDisplayType): boolean {
        return (this.getDisplayType() & displayType) === displayType;
    }

    private updateDisplayTypeVisibility() {
        this.$iconRow.toggle(this.hasDisplayType(AttributeDisplayType.Icon));
        this.$displayToRow.toggle(this.hasDisplayType(AttributeDisplayType.Flag));
        this.$effectRow.toggle(this.hasDisplayType(AttributeDisplayType.Filter));
    }

    private updateEffectHint() {
        const effect = Number(this.effectSelect.getValue() ?? AttributeEffect.Include);
        this.$effectHint.text(i18n("attribute.effectDescription." + AttributeEffect[effect]));
    }

    private effectDataSource: IDataSource<{ type: AttributeEffect }> = {
        getData: () => {
            const items = [{ type: AttributeEffect.Include }, { type: AttributeEffect.Exclude }];

            return Promise.resolve({ items, offset: 0, limit: items.length, hasNext: false, hasPrev: false });
        },
    };

    private displayTypeDataSource: IDataSource<{ type: AttributeDisplayType }> = {
        getData: () => {
            const items = [
                { type: AttributeDisplayType.Icon },
                { type: AttributeDisplayType.Flag },
                { type: AttributeDisplayType.Filter },
            ];

            return Promise.resolve({ items, offset: 0, limit: items.length, hasNext: false, hasPrev: false });
        },
    };

    private displayToDataSource: IDataSource<{ type: AttributeFlagDisplayTo }> = {
        getData: () => {
            const items = [
                { type: AttributeFlagDisplayTo.EndUsersAndOperators },
                { type: AttributeFlagDisplayTo.EndUsers },
                { type: AttributeFlagDisplayTo.Operators },
            ];

            return Promise.resolve({ items, offset: 0, limit: items.length, hasNext: false, hasPrev: false });
        },
    };

    private static splitDisplayType(displayType: AttributeDisplayType): Array<number> {
        const values: number[] = [];
        Misc.eachEnum(AttributeDisplayType, (id) => {
            if (id === AttributeDisplayType.None) return;
            if ((displayType & id) === id) values.push(id);
        });

        return values;
    }
}
